// services/apiMonitor.js — API Cost & Usage Monitor
const LS_KEY = 'sm_api_monitor';

// Pricing in EUR (converted from USD at ~0.92 rate, Feb 2026)
const USD_TO_EUR = 0.92;
const API_PRICING = {
  claude: {
    inputPerMToken: +(3.00 * USD_TO_EUR).toFixed(2),   // ~2.76 €/M tokens
    outputPerMToken: +(15.00 * USD_TO_EUR).toFixed(2),  // ~13.80 €/M tokens
    webSearchPerCall: +(0.01 * USD_TO_EUR).toFixed(4),  // ~0.0092 €/call
  },
  finnhub: { costPerCall: 0, maxPerMin: 60 },
  twelvedata: { costPerCall: 0, maxPerDay: 800, maxPerMin: 8 },
  yahoo: { costPerCall: 0 },
  macro: { costPerCall: 0 },
};

const DATA_PROVIDERS = ['finnhub', 'twelvedata', 'yahoo', 'macro'];

function createEmptyStats() {
  const stats = {
    claude: { calls: 0, inputTokens: 0, outputTokens: 0, webSearches: 0, errors: 0, lastCall: null },
  };
  DATA_PROVIDERS.forEach(p => {
    stats[p] = { calls: 0, cacheHits: 0, errors: 0, lastCall: null };
  });
  return stats;
}

function loadData() {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

function saveData(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (e) { console.warn('[ApiMonitor] save failed:', e); }
}

function todayKey() { return new Date().toISOString().slice(0, 10); }

function getMonitorData() {
  const saved = loadData();
  const today = todayKey();
  if (saved && saved.version === 1) {
    if (!saved.daily[today]) saved.daily[today] = createEmptyStats();
    return saved;
  }
  return { version: 1, startDate: today, daily: { [today]: createEmptyStats() }, session: createEmptyStats(), sessionStart: new Date().toISOString() };
}

let monitorData = getMonitorData();

function trackProvider(provider, usage = {}, fromCache = false, hasWebSearch = false, isError = false) {
  const today = todayKey();
  if (!monitorData.daily[today]) monitorData.daily[today] = createEmptyStats();
  const targets = [monitorData.daily[today][provider], monitorData.session[provider]];
  const now = new Date().toISOString();
  targets.forEach(t => {
    if (provider === 'claude') {
      t.calls += 1;
      t.inputTokens += usage.input_tokens || 0;
      t.outputTokens += usage.output_tokens || 0;
      if (hasWebSearch) t.webSearches += 1;
    } else {
      if (fromCache) { t.cacheHits += 1; } else { t.calls += 1; }
    }
    if (isError) t.errors += 1;
    t.lastCall = now;
  });
  saveData(monitorData);
}

// ── Track wrappers (generated dynamically) ───────────────────
export function trackClaude(usage = {}, hasWebSearch = false, isError = false) {
  trackProvider('claude', usage, false, hasWebSearch, isError);
}
// Data providers share the same signature: (fromCache, isError)
const makeTracker = (name) => (fromCache = false, isError = false) =>
  trackProvider(name, {}, fromCache, false, isError);

export const trackFinnhub = makeTracker('finnhub');
export const trackTwelveData = makeTracker('twelvedata');
export const trackYahoo = makeTracker('yahoo');
export const trackMacro = makeTracker('macro');

export function getTodayStats() {
  return monitorData.daily[todayKey()] || createEmptyStats();
}
export function getSessionStats() { return monitorData.session; }

export function getAllTimeStats() {
  const agg = createEmptyStats();
  Object.values(monitorData.daily).forEach(day => {
    // Claude (special: has tokens + webSearches)
    agg.claude.calls += day.claude.calls;
    agg.claude.errors += day.claude.errors;
    agg.claude.inputTokens += day.claude.inputTokens;
    agg.claude.outputTokens += day.claude.outputTokens;
    agg.claude.webSearches += day.claude.webSearches;
    // Data providers (same shape)
    DATA_PROVIDERS.forEach(p => {
      if (!day[p]) return;
      agg[p].calls += day[p].calls;
      agg[p].errors += day[p].errors;
      agg[p].cacheHits += day[p].cacheHits;
    });
  });
  return agg;
}

export function calculateCosts(stats) {
  const c = stats.claude;
  const inputCost = (c.inputTokens / 1000000) * API_PRICING.claude.inputPerMToken;
  const outputCost = (c.outputTokens / 1000000) * API_PRICING.claude.outputPerMToken;
  const searchCost = c.webSearches * API_PRICING.claude.webSearchPerCall;
  return {
    claude: { input: inputCost, output: outputCost, search: searchCost, total: inputCost + outputCost + searchCost },
    finnhub: { total: 0 }, twelvedata: { total: 0 }, yahoo: { total: 0 },
    grandTotal: inputCost + outputCost + searchCost,
  };
}

export function getTwelveDataRateStatus() {
  const stats = monitorData.daily[todayKey()]?.twelvedata || { calls: 0 };
  return { used: stats.calls, limit: API_PRICING.twelvedata.maxPerDay, percentage: Math.round((stats.calls / API_PRICING.twelvedata.maxPerDay) * 100) };
}

export function getDailyHistory(days = 7) {
  return Object.entries(monitorData.daily)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, days)
    .map(([date, stats]) => ({ date, ...stats, costs: calculateCosts(stats) }));
}

export function getSessionInfo() {
  return { start: monitorData.sessionStart, dataStart: monitorData.startDate, daysTracked: Object.keys(monitorData.daily).length };
}

export function resetSession() {
  monitorData.session = createEmptyStats();
  monitorData.sessionStart = new Date().toISOString();
  saveData(monitorData);
}

export function resetAll() {
  localStorage.removeItem(LS_KEY);
  monitorData = getMonitorData();
}

export function getPricing() { return API_PRICING; }
